import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
} from "@mui/material";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import RefreshIcon from "@mui/icons-material/Refresh";
import { verifyEmail, resendCode } from "../../../Services/apiService";
import logo from "../../../Assets/logo.png";

const ReactivateAccount = () => {
  const { state } = useLocation();
  const { email, userid } = state || {};
  const navigate = useNavigate();

  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [timer, setTimer] = useState(60);
  const [isResendVisible, setIsResendVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);

  // إرسال الكود تلقائياً عند فتح الصفحة
  useEffect(() => {
    const sendInitialCode = async () => {
      if (email) {
        try {
          const response = await resendCode(email);
          setSuccess(
            response.data?.message ||
              "📩 تم إرسال رمز التحقق إلى بريدك الإلكتروني."
          );
        } catch (err) {
          console.error("Error sending initial code:", err);
          // لا نعرض خطأ هنا لأن الكود قد يكون موجود بالفعل
        }
      }
    };

    sendInitialCode();
  }, [email]);

  // تشغيل العداد
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setIsResendVisible(true);
    }
  }, [timer]);

  // تحقق من الكود
  const handleVerifyCode = async () => {
    if (!userid || !code.trim()) {
      setError("الرجاء إدخال رمز التحقق.");
      return;
    }

    try {
      setLoading(true);
      const response = await verifyEmail(userid, code.trim());

      setSuccess("✅ تم التحقق من الكود بنجاح! سيتم تحويلك لتسجيل الدخول...");
      setError("");

      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(
        err.response?.data?.message || "الكود المدخل غير صحيح. حاول مرة أخرى."
      );
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  // إعادة إرسال الكود
  const handleResendCode = async () => {
    if (!email) {
      setError("البريد الإلكتروني غير متوفر.");
      return;
    }

    try {
      setResendLoading(true);
      const response = await resendCode(email);

      setSuccess(
        response.data?.message ||
          "📩 تم إرسال رمز تحقق جديد إلى بريدك الإلكتروني."
      );
      setError("");
      setTimer(60);
      setIsResendVisible(false);
    } catch (err) {
      setError(err.response?.data?.message || "حدث خطأ أثناء إرسال الكود.");
      setSuccess("");
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e8eef5 100%)",
        position: "relative",
        py: 6,
        px: 2,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage:
            "radial-gradient(circle, rgba(243, 111, 33, 0.03) 1px, transparent 1px)",
          backgroundSize: "30px 30px",
          pointerEvents: "none",
        },
      }}
    >
      <Container maxWidth="xs" sx={{ position: "relative", zIndex: 1 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 4, md: 5 },
            borderRadius: 5,
            border: "2px solid #e8e8e8",
            background: "white",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
            transition: "all 0.3s ease",
            "&:hover": {
              boxShadow: "0 12px 48px rgba(243, 111, 33, 0.12)",
              borderColor: "#f36f21",
            },
          }}
        >
          {/* Logo & App Name */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                height: { xs: 70, md: 90 },
                width: "auto",
                mb: 2,
                filter: "drop-shadow(0 4px 8px rgba(243, 111, 33, 0.2))",
              }}
            />
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textAlign: "center",
                fontSize: { xs: "1.5rem", md: "2rem" },
              }}
            >
              ريستو كاش
            </Typography>
          </Box>

          {/* Icon Header */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(243, 111, 33, 0.1) 0%, rgba(230, 81, 0, 0.1) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <VerifiedUserIcon sx={{ fontSize: 30, color: "#f36f21" }} />
            </Box>
          </Box>

          <Typography
            variant="h5"
            align="center"
            gutterBottom
            fontWeight={800}
            sx={{
              color: "text.primary",
              mb: 1,
            }}
          >
            إعادة تفعيل الحساب
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{ mb: 3, color: "text.secondary", lineHeight: 1.8 }}
          >
            أدخل رمز التحقق الذي أرسلناه إلى بريدك الإلكتروني
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "error.light",
              }}
            >
              {error}
            </Alert>
          )}
          {success && (
            <Alert
              severity="success"
              sx={{
                mb: 3,
                borderRadius: 3,
                border: "1px solid",
                borderColor: "success.light",
              }}
            >
              {success}
            </Alert>
          )}

          <TextField
            label="رمز التحقق"
            variant="outlined"
            fullWidth
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="أدخل الكود المكون من 6 أرقام"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MarkEmailReadIcon sx={{ color: "#f36f21" }} />
                </InputAdornment>
              ),
              sx: {
                fontSize: "1.2rem",
                textAlign: "center",
                letterSpacing: "0.5rem",
              },
            }}
            sx={{
              mb: 3,
              "& .MuiOutlinedInput-root": {
                borderRadius: 3,
                "&:hover fieldset": { borderColor: "#f36f21" },
                "&.Mui-focused fieldset": {
                  borderColor: "#f36f21",
                  borderWidth: 2,
                },
              },
            }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleVerifyCode}
            disabled={loading}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : <VerifiedUserIcon />
            }
            sx={{
              py: 2,
              fontSize: "1.1rem",
              fontWeight: 700,
              borderRadius: 3,
              textTransform: "none",
              background: "linear-gradient(135deg, #f36f21 0%, #e65100 100%)",
              boxShadow: "0 6px 20px rgba(243, 111, 33, 0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #e65100 0%, #d84315 100%)",
                boxShadow: "0 8px 28px rgba(243, 111, 33, 0.4)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "rgba(243, 111, 33, 0.5)",
              },
            }}
          >
            {loading ? "جاري التحقق..." : "تحقق من الكود"}
          </Button>

          {!isResendVisible ? (
            <Box
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 3,
                background: "linear-gradient(135deg, rgba(76, 175, 80, 0.05) 0%, rgba(67, 160, 71, 0.05) 100%)",
                border: "1px solid rgba(76, 175, 80, 0.2)",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#388e3c", fontWeight: 600 }}
              >
                ⏳ يمكنك طلب كود جديد بعد {timer} ثانية
              </Typography>
            </Box>
          ) : (
            <Button
              variant="outlined"
              fullWidth
              onClick={handleResendCode}
              disabled={resendLoading}
              startIcon={
                resendLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
              }
              sx={{
                mt: 3,
                py: 1.8,
                fontSize: "1rem",
                fontWeight: 600,
                borderRadius: 3,
                textTransform: "none",
                borderWidth: 2,
                borderColor: "#f36f21",
                color: "#f36f21",
                "&:hover": {
                  borderWidth: 2,
                  borderColor: "#e65100",
                  background: "rgba(243, 111, 33, 0.05)",
                  transform: "translateY(-2px)",
                },
                "&:disabled": {
                  borderColor: "rgba(243, 111, 33, 0.3)",
                  color: "rgba(243, 111, 33, 0.5)",
                },
              }}
            >
              {resendLoading ? "جاري الإرسال..." : "إعادة إرسال رمز التفعيل"}
            </Button>
          )}

          <Box
            sx={{
              mt: 4,
              p: 3,
              borderRadius: 3,
              background: "linear-gradient(135deg, rgba(243, 111, 33, 0.05) 0%, rgba(230, 81, 0, 0.05) 100%)",
              border: "1px solid rgba(243, 111, 33, 0.15)",
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.8 }}>
              💡 <strong>نصيحة:</strong> تحقق من مجلد الرسائل غير المرغوب فيها (Spam) إذا لم تجد الرسالة.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ReactivateAccount;
